import { useEffect, useState } from "react";
import api from "../api";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Bounce, toast } from "react-toastify";
import "./esperienzeLavorative.css";
import CustomSelect from "../CustomSelect";

const EditEsperienzeLavorative = () => {
  const [loading, setLoading] = useState(true);
  const [esperienza, setEsperienza] = useState(null);
  const [mansioni, setMansioni] = useState([]);

  const [form, setForm] = useState({
    ruolo: "",
    mansioneId: "",
    dal: "",
    al: "",
    nomeAzienda: "",
    descrizioneRuolo: "",
  });

  const [errors, setErrors] = useState({});
  const [editMode, setEditMode] = useState(false);

  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Convert "dd.mm.yyyy" to "yyyy-mm-dd"
  const toISODate = (dateStr) => {
    if (!dateStr) return "";
    const [day, month, year] = dateStr.split(".");
    if (!day || !month || !year) return "";
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  };

  // Convert "yyyy-mm-dd" to "dd.mm.yyyy"
  const fromISODate = (isoStr) => {
    if (!isoStr) return "";
    const [year, month, day] = isoStr.split("-");
    if (!year || !month || !day) return "";
    return `${day.padStart(2, "0")}.${month.padStart(2, "0")}.${year}`;
  };

  // Capitalize first letter only helper
  const capitalizeFirstLetter = (str) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");

    if (userRole !== "TRAINER") {
      alert("Accedi come docente per modificare le esperienze lavorative!");
      navigate("/login");
      localStorage.clear();
      return;
    }

    if (location.state?.esperienza) {
      const data = location.state.esperienza;

      setEsperienza(data);
      setForm({
        ruolo: data.ruolo || "",
        mansioneId: data.mansione?.idMansione || "",
        dal: data.dal || "",
        al: data.al || "",
        nomeAzienda: data.nomeAzienda || "",
        descrizioneRuolo: data.descrizioneRuolo || "",
      });
    } else {
      toast.error("Dati esperienza lavorativa non trovati!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    }

    fetchMansioni();
  }, [location.state, navigate]);

  const fetchMansioni = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/mansioni`);
      if (response.status === 200) {
        setMansioni(response.data);
      } else {
        toast.error("Errore nel recupero delle mansioni.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let newValue = value;
    if ((name === "dal" || name === "al") && editMode) {
      // Dates are stored/displayed as dd.mm.yyyy in form
      newValue = fromISODate(value);
    }

    setForm((prev) => ({ ...prev, [name]: newValue }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const validate = () => {
    const inputErrors = {};
    if (!form.ruolo.trim()) inputErrors.ruolo = "Ruolo non può essere vuoto!";
    if (!form.mansioneId)
      inputErrors.mansioneId = "Mansione non può essere vuota!";
    if (!form.dal.trim()) inputErrors.dal = "La data di inizio è obbligatoria.";
    if (!form.nomeAzienda.trim())
      inputErrors.nomeAzienda = "Nome azienda non può essere vuoto!";
    if (!form.descrizioneRuolo.trim())
      inputErrors.descrizioneRuolo = "Descrizione ruolo non può essere vuota!";
    setErrors(inputErrors);
    return Object.keys(inputErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const convertToISO = (dateStr) => {
      if (!dateStr) return "";
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
      if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
        const [day, month, year] = dateStr.split(".");
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      }
      return dateStr;
    };

    try {
      setLoading(true);

      const payload = {
        ...form,
        ruolo: form.ruolo ? form.ruolo.toUpperCase() : "",
        dal: convertToISO(form.dal),
        al: convertToISO(form.al),
      };

      const response = await api.put(`/esperienze-lavorative/${id}`, payload);
      if (response.status === 200) {
        toast.success("Esperienza aggiornata con successo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        setEsperienza({ ...esperienza, ...form });
        setEditMode(false);
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (esperienza) {
      setForm({
        ruolo: esperienza.ruolo || "",
        mansioneId: esperienza.mansione?.idMansione || "",
        dal: esperienza.dal || "",
        al: esperienza.al || "",
        nomeAzienda: esperienza.nomeAzienda || "",
        descrizioneRuolo: esperienza.descrizioneRuolo || "",
      });
    }
    setErrors({});
    setEditMode(false);
  };

  if (loading || !esperienza) {
    return (
      <div className="elav-modern-overlay">
        <div className="elav-modern-spinner"></div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return "";

    const parts = dateStr.split(".");
    if (parts.length !== 3) return dateStr;

    const [day, month, year] = parts;

    return `${day}.${month}.${year}`;
  };

  const formatCreatoIl = (dateString) => {
    if (!dateString) return "";

    const isoDatePart = dateString.slice(0, 10);
    const [year, month, day] = isoDatePart.split("-");
    if (!year || !month || !day) return "";

    return `${day}.${month}.${year}`;
  };

  return (
    <div className="elav-modern-bg">
      {loading && (
        <div className="elav-modern-overlay">
          <div className="elav-modern-spinner"></div>
        </div>
      )}
      <div className="elav-modern-card edit-esperienza">
        <div className="elav-modern-header">
          <button
            onClick={() => navigate("/esperienze-lavorative")}
            className="elav-modern-back"
            title="Torna indietro"
          >
            <i className="fa-solid fa-arrow-left"></i>
          </button>
          <h2>
            <i className="fa-solid fa-briefcase"></i> Modifica Esperienza
            Lavorativa
          </h2>
          {!editMode && (
            <button
              className="elav-modern-edit"
              onClick={() => setEditMode(true)}
              title="Modifica"
            >
              <i className="fa-solid fa-pen"></i> Modifica
            </button>
          )}
        </div>
        <form
          className="elav-modern-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <div className="elav-modern-fields">
            {[
              {
                label: "Ruolo",
                name: "ruolo",
                type: "selectRuolo",
                icon: "fa-user-tie",
                options: [
                  { value: "TITOLARE", label: "Titolare" },
                  { value: "DIPENDENTE", label: "Dipendente" },
                  { value: "CONSULENTE", label: "Consulente" },
                  { value: "DOCENTE", label: "Docente" },
                ],
              },
              {
                label: "Mansione",
                name: "mansioneId",
                type: "select",
                icon: "fa-tasks",
                options: mansioni.map((m) => ({
                  value: m.idMansione,
                  label: m.nomeMansione,
                })),
              },
              {
                label: "Data Inizio",
                name: "dal",
                type: "date",
                icon: "fa-calendar-plus",
              },
              {
                label: "Data Fine",
                name: "al",
                type: "date",
                icon: "fa-calendar-minus",
              },
              {
                label: "Nome Azienda",
                name: "nomeAzienda",
                icon: "fa-building",
              },
              {
                label: "Descrizione del ruolo",
                name: "descrizioneRuolo",
                type: "textarea",
                icon: "fa-align-left",
              },
            ].map(({ label, name, type, options, icon }) => (
              <div className="elav-modern-field" key={name}>
                <label className="elav-modern-label">
                  <i className={`fa-solid ${icon}`}></i>
                  {label}
                </label>
                {editMode ? (
                  type === "textarea" ? (
                    <textarea
                      id={name}
                      name={name}
                      value={form[name]}
                      onChange={handleChange}
                      rows={4}
                      className={errors[name] ? "error" : ""}
                    />
                  ) : type === "selectRuolo" ? (
                    <CustomSelect
                      options={[
                        { value: "TITOLARE", label: "Titolare" },
                        { value: "DIPENDENTE", label: "Dipendente" },
                        { value: "CONSULENTE", label: "Consulente" },
                        { value: "DOCENTE", label: "Docente" },
                      ]}
                      value={
                        form[name]
                          ? {
                              value: form[name].toUpperCase(),
                              label:
                                [
                                  { value: "TITOLARE", label: "Titolare" },
                                  { value: "DIPENDENTE", label: "Dipendente" },
                                  { value: "CONSULENTE", label: "Consulente" },
                                  { value: "DOCENTE", label: "Docente" },
                                ].find(
                                  (opt) =>
                                    opt.value === form[name].toUpperCase()
                                )?.label || capitalizeFirstLetter(form[name]),
                            }
                          : null
                      }
                      onChange={(selected) =>
                        handleChange({
                          target: {
                            name,
                            value: selected ? selected.value : "",
                          },
                        })
                      }
                      placeholder="-- Seleziona ruolo --"
                    />
                  ) : type === "select" ? (
                    <CustomSelect
                      options={mansioni.map((m) => ({
                        value: m.idMansione,
                        label: m.nomeMansione,
                      }))}
                      value={
                        form[name]
                          ? {
                              value: form[name],
                              label:
                                mansioni.find(
                                  (opt) => opt.idMansione === form[name]
                                )?.nomeMansione || form[name],
                            }
                          : null
                      }
                      onChange={(selected) =>
                        handleChange({
                          target: {
                            name,
                            value: selected ? selected.value : "",
                          },
                        })
                      }
                      placeholder="-- Seleziona --"
                    />
                  ) : (
                    <input
                      id={name}
                      name={name}
                      type={type || "text"}
                      value={
                        editMode && (name === "dal" || name === "al")
                          ? toISODate(form[name])
                          : form[name]
                      }
                      onChange={handleChange}
                      className={errors[name] ? "error" : ""}
                    />
                  )
                ) : (
                  <span className="elav-modern-value">
                    {name === "ruolo"
                      ? capitalizeFirstLetter(
                          location.state?.esperienza?.ruolo
                        ) || "-"
                      : type === "select"
                      ? esperienza.nomeMansione
                      : name === "dal" || name === "al"
                      ? formatDate(esperienza[name])
                      : esperienza[name] || "-"}
                  </span>
                )}
                {errors[name] && (
                  <span className="elav-modern-error">
                    <i className="fa-solid fa-exclamation-triangle"></i>
                    {errors[name]}
                  </span>
                )}
              </div>
            ))}
            {!editMode && (
              <>
                <div className="elav-modern-field">
                  <label className="elav-modern-label">
                    <i className="fa-solid fa-align-justify"></i> Descrizione
                    della mansione
                  </label>
                  <span className="elav-modern-value">
                    {esperienza.descrizioneMansione}
                  </span>
                </div>
                <div className="elav-modern-field">
                  <label className="elav-modern-label">
                    <i className="fa-solid fa-id-card"></i> Codice Fiscale
                  </label>
                  <span className="elav-modern-value">
                    {esperienza.trainerCodiceFiscale}
                  </span>
                </div>
                <div className="elav-modern-field">
                  <label className="elav-modern-label">
                    <i className="fa-solid fa-calendar"></i> Creato il
                  </label>
                  <span className="elav-modern-value">
                    {formatCreatoIl(esperienza.dataCreazione)}
                  </span>
                </div>
                <div className="elav-modern-field">
                  <label className="elav-modern-label">
                    <i className="fa-solid fa-user"></i> Autore
                  </label>
                  <span className="elav-modern-value">
                    {esperienza.autoreInserimento}
                  </span>
                </div>
              </>
            )}
          </div>

          {editMode && (
            <div className="elav-modern-actions">
              <button
                type="button"
                className="elav-modern-cancel"
                onClick={handleCancel}
              >
                <i className="fa-solid fa-times"></i> Annulla
              </button>
              <button type="submit" className="elav-modern-save">
                <i className="fa-solid fa-save"></i> Salva Modifica
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default EditEsperienzeLavorative;
